import { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';
import { validateTransactionForm, US_STATES } from '../utils/validation';
import { isCompleteTransactionStatus, isRejectedTransactionStatus } from '../utils/transactionStatus';

// Format date string without timezone conversion
const formatDateString = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  
  // If it's an ISO string, extract the date part (YYYY-MM-DD)
  // This avoids timezone conversion
  if (typeof dateString === 'string') {
    // Handle ISO format: "2025-01-10T00:00:00.000Z" -> "2025-01-10"
    const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      const [year, month, day] = dateMatch[1].split('-');
      return `${month}/${day}/${year}`;
    }
    // If already in a different format, return as is
    return dateString;
  }
  
  return '-';
};

/** Value for `<input type="date">` — uses calendar YYYY-MM-DD from ISO without local TZ shift. */
function toDateInputValue(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const m = String(dateString).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

/** Store edited calendar date as UTC midnight ISO (aligns with @db.Date / Prisma). */
function dateInputToIso(dateInput: string): string {
  return `${dateInput}T00:00:00.000Z`;
}

/** Local calendar today as UTC midnight ISO for `processingCompleteAt` when status becomes Complete. */
function utcMidnightIsoForLocalToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00:00.000Z`;
}

/** Parses agency JSON text columns (e.g. transactionTypes, statuses) from API responses. */
function parseAgencyJsonStringArray(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === 'string');
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((x): x is string => typeof x === 'string')
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Shared typography for the Transactions results table (card) — body and headers use the same size. */
const TX_GRID_TEXT: CSSProperties = {
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  lineHeight: 1.5,
};

function txGridHeaderStyle(extra?: CSSProperties): CSSProperties {
  return {
    padding: '16px 12px',
    textAlign: 'left',
    ...TX_GRID_TEXT,
    fontWeight: 600,
    color: 'var(--secondary-color)',
    ...extra,
  };
}

function txGridCellStyle(extra?: CSSProperties): CSSProperties {
  return {
    padding: '16px 12px',
    ...TX_GRID_TEXT,
    fontWeight: 400,
    ...extra,
  };
}

/** Keys accepted by `GET /titles/search/paginated` for `sortBy`. */
type TransactionSortKey =
  | 'vin'
  | 'agency'
  | 'group'
  | 'clientNumber'
  | 'unitNumber'
  | 'eta'
  | 'status'
  | 'completedAt'
  | 'transactionType'
  | 'state'
  | 'vendor'
  | 'requestor'
  | 'requestReceived';

type TransactionTableSort = { key: TransactionSortKey | null; dir: 'asc' | 'desc' };

function TransactionsSortableColumnHeader({
  column,
  label,
  sort,
  onSort,
  extra,
}: {
  column: TransactionSortKey;
  label: string;
  sort: TransactionTableSort;
  onSort: (column: TransactionSortKey) => void;
  extra?: CSSProperties;
}) {
  const isActive = sort.key === column;
  const title = isActive
    ? sort.dir === 'asc'
      ? 'Sorted A–Z. Click for Z–A.'
      : 'Sorted Z–A. Click for A–Z.'
    : 'Sort A–Z (click again for Z–A)';

  return (
    <th
      role="columnheader"
      aria-sort={isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={title}
      onClick={() => onSort(column)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(column);
        }
      }}
      tabIndex={0}
      style={{
        ...txGridHeaderStyle(extra),
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {label}
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: '18px',
            lineHeight: 1,
            opacity: isActive ? 1 : 0.4,
            fontVariationSettings: '"FILL" 0, "wght" 500, "GRAD" 0, "opsz" 24',
          }}
          aria-hidden
        >
          {isActive ? (sort.dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'swap_vert'}
        </span>
      </span>
    </th>
  );
}

interface Transaction {
  id: string;
  vin: string;
  vendorName?: string;
  clientNumber?: string;
  unitNumber?: string;
  registrationState?: string;
  transactionType?: string;
  requestor?: string;
  requestTrackingNumber?: string;
  status: string;
  notes?: string;
  agencyTrackingNumber?: string;
  tempTagNumber?: string;
  licensePlateNumber?: string;
  licensePlateState?: string;
  invoiceNumber?: string;
  requestReceivedAt?: string;
  estimatedCompleteAt?: string | null;
  processingCompleteAt?: string;
  sentAgencyAt?: string;
  tempTagExpirationAt?: string;
  licensePlateExpirationAt?: string;
  createdAt: string;
  updatedAt: string;
  agency: {
    id: string;
    name: string;
    code: string;
  };
  group?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

/** Date fields: only include in PUT when the form has a value (see handleUpdateWork — no `undefined` → `null`). */
const TRANSACTION_PUT_DATE_KEYS: (keyof Transaction)[] = [
  'requestReceivedAt',
  'estimatedCompleteAt',
  'processingCompleteAt',
  'sentAgencyAt',
  'tempTagExpirationAt',
  'licensePlateExpirationAt',
];

interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface SearchResponse {
  data: Transaction[];
  pagination: PaginationInfo;
}

interface TransactionCounts {
  total: number;
  byStatus: Record<string, number>;
}

type DashboardTxScope = 'all' | 'agency' | 'group';

/** Background refresh interval — skips loading UI; paused when tab hidden or modals open. */
const DASHBOARD_POLL_MS = 45_000;

function Dashboard() {
  const { user: currentUser } = useUser();

  const {
    transactionListScope,
    isCustomerReader,
    canPickAgencyFilter,
    isSystemAdmin,
    showGroupColumn,
  } = useMemo(() => {
    const roles = currentUser?.roles ?? [];
    const ids = new Set(roles.map((r) => r.id));
    const sys = ids.has('admin-role-id');
    const cust = ids.has('customer-role-id') || ids.has('customer-admin-role-id');
    let scope: DashboardTxScope = 'agency';
    if (sys) scope = 'all';
    else if (cust) scope = 'group';
    else if (ids.has('agency-admin-role-id') || ids.has('agency-user-role-id')) scope = 'agency';
    else if (ids.has('group-admin-role-id') || ids.has('group-user-role-id')) scope = 'group';
    return {
      transactionListScope: scope,
      isCustomerReader: cust,
      canPickAgencyFilter: sys,
      isSystemAdmin: sys,
      showGroupColumn:
        ids.has('admin-role-id') ||
        ids.has('agency-admin-role-id') ||
        ids.has('agency-user-role-id'),
    };
  }, [currentUser?.roles]);

  const isAdmin = currentUser?.roles?.some(
    (role) => role.id === 'admin-role-id' || role.id === 'agency-admin-role-id',
  );

  const canMutateTransactions = !isCustomerReader;

  const actionsColumnWidthPx =
    !canMutateTransactions ? 56 : isAdmin ? 168 : 120;

  const [searchParams, setSearchParams] = useState({
    vin: '',
    agency: '',
    status: '',
    clientNumber: '',
    unitNumber: '',
    requestor: '',
    requestReceivedAt: '',
  });
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availableTransactionTypes, setAvailableTransactionTypes] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [agencies, setAgencies] = useState<Array<{id: string, name: string, statuses?: string, transactionTypes?: string}>>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [txTableSort, setTxTableSort] = useState<TransactionTableSort>({ key: null, dir: 'asc' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionCounts, setTransactionCounts] = useState<TransactionCounts>({
    total: 0,
    byStatus: {}
  });
  const [countsLoading, setCountsLoading] = useState(false);
  const [editingWork, setEditingWork] = useState<Transaction | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Transaction>>({});
  const [viewingWork, setViewingWork] = useState<Transaction | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [transactionPendingDelete, setTransactionPendingDelete] = useState<Transaction | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  /** Poll + tab-focus refresh reads latest filters/pagination without resetting interval on each keystroke. */
  const searchParamsRef = useRef(searchParams);
  const paginationRef = useRef(pagination);
  const txTableSortRef = useRef(txTableSort);
  const loadingRef = useRef(loading);
  const blockAutoRefreshRef = useRef(false);
  const refreshDashboardSilentRef = useRef<() => void>(() => {});
  /** True after user changes Estimated Complete At (avoids sending accidental null from status-driven state). */
  const editEtaTouchedRef = useRef(false);

  useEffect(() => {
    searchParamsRef.current = searchParams;
    paginationRef.current = pagination;
  }, [searchParams, pagination]);

  useEffect(() => {
    txTableSortRef.current = txTableSort;
  }, [txTableSort]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    blockAutoRefreshRef.current =
      isEditModalOpen ||
      isViewModalOpen ||
      transactionPendingDelete !== null ||
      deleteInProgress;
  }, [isEditModalOpen, isViewModalOpen, transactionPendingDelete, deleteInProgress]);

  useEffect(() => {
    if (currentUser?.agency) {
      console.log('Dashboard - Current user agency:', currentUser.agency);

      setSearchParams((prev) => ({
        ...prev,
        agency: canPickAgencyFilter ? '' : currentUser.agency.id,
      }));

      if (!canPickAgencyFilter) {
        setAvailableStatuses(parseAgencyJsonStringArray(currentUser.agency.statuses));
        setAvailableTransactionTypes(parseAgencyJsonStringArray(currentUser.agency.transactionTypes));
      }

      loadTransactions();
      loadTransactionCounts();

      if (canPickAgencyFilter) {
        fetchAgencies();
      }
    } else {
      console.log('Dashboard - No current user or agency found');
    }
  }, [currentUser, canPickAgencyFilter]);

  // Load counts when search parameters change
  useEffect(() => {
    if (currentUser?.agency) {
      loadTransactionCounts();
    }
  }, [searchParams.vin, searchParams.agency, searchParams.clientNumber, searchParams.unitNumber, searchParams.requestor, searchParams.requestReceivedAt]);

  // Update statuses when agency changes (global scope: system admin or customer reader)
  useEffect(() => {
    if (canPickAgencyFilter && searchParams.agency && agencies.length > 0) {
      const selectedAgency = agencies.find((a) => a.id === searchParams.agency);
      if (selectedAgency) {
        setAvailableStatuses(parseAgencyJsonStringArray(selectedAgency.statuses));
        setAvailableTransactionTypes(parseAgencyJsonStringArray(selectedAgency.transactionTypes));
      }
    } else if (canPickAgencyFilter && !searchParams.agency) {
      setAvailableStatuses([]);
      setAvailableTransactionTypes([]);
    }
  }, [searchParams.agency, agencies, canPickAgencyFilter]);
  
  console.log('Dashboard - User roles:', currentUser?.roles);
  console.log('Dashboard - Is admin:', isAdmin);

  const fetchAgencies = async () => {
    if (!canPickAgencyFilter) return;
    
    try {
      const response = await api.get('/agencies');
      setAgencies(response.data);
      
      // If an agency is already selected, update statuses
      if (searchParams.agency && response.data.length > 0) {
        const selectedAgency = response.data.find((a: any) => a.id === searchParams.agency);
        if (selectedAgency) {
          setAvailableStatuses(parseAgencyJsonStringArray(selectedAgency.statuses));
          setAvailableTransactionTypes(parseAgencyJsonStringArray(selectedAgency.transactionTypes));
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch agencies:', err);
    }
  };

  type LoadOptions = { silent?: boolean };

  const loadTransactionCounts = async (customParams?: typeof searchParams, options?: LoadOptions) => {
    if (!currentUser?.agency) return;

    const silent = options?.silent === true;
    if (!silent) {
      setCountsLoading(true);
    }

    try {
      // Use customParams if provided, otherwise use current searchParams
      const paramsToUse = customParams || searchParams;
      
      const params = new URLSearchParams();

      if (paramsToUse.vin) {
        params.append('vin', paramsToUse.vin);
      }
      if (paramsToUse.clientNumber) {
        params.append('clientNumber', paramsToUse.clientNumber);
      }
      if (paramsToUse.unitNumber) {
        params.append('unitNumber', paramsToUse.unitNumber);
      }
      if (paramsToUse.requestor) {
        params.append('requestor', paramsToUse.requestor);
      }
      if (paramsToUse.requestReceivedAt) {
        params.append('requestReceivedAt', paramsToUse.requestReceivedAt);
      }

      if (transactionListScope === 'all') {
        if (paramsToUse.agency) {
          params.append('agencyId', paramsToUse.agency);
        }
      } else if (currentUser?.agency) {
        params.append('agencyId', currentUser.agency.id);
      }

      const response = await api.get(`/titles/counts?${params.toString()}`);
      setTransactionCounts(response.data);
    } catch (err: any) {
      console.error('Failed to load transaction counts:', err);
    } finally {
      if (!silent) {
        setCountsLoading(false);
      }
    }
  };

  const loadTransactions = async (
    page: number = pagination.currentPage,
    pageSize: number = pagination.pageSize,
    options?: LoadOptions,
  ) => {
    if (!currentUser?.agency) return;

    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (searchParams.vin) {
        params.append('vin', searchParams.vin);
      }
      if (searchParams.status) {
        params.append('status', searchParams.status);
      }
      if (searchParams.clientNumber) {
        params.append('clientNumber', searchParams.clientNumber);
      }
      if (searchParams.unitNumber) {
        params.append('unitNumber', searchParams.unitNumber);
      }
      if (searchParams.requestor) {
        params.append('requestor', searchParams.requestor);
      }
      if (searchParams.requestReceivedAt) {
        params.append('requestReceivedAt', searchParams.requestReceivedAt);
      }
      if (transactionListScope === 'all') {
        if (searchParams.agency) {
          params.append('agencyId', searchParams.agency);
        }
      } else if (currentUser?.agency) {
        params.append('agencyId', currentUser.agency.id);
      }

      const sort = txTableSortRef.current;
      if (sort.key) {
        params.append('sortBy', sort.key);
        params.append('sortDir', sort.dir);
      }

      const response = await api.get(`/titles/search/paginated?${params.toString()}`);
      const data: SearchResponse = response.data;

      setTransactions(data.data);
      setPagination(data.pagination);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      if (!silent) {
        setError(err.response?.data?.message || 'Failed to load transactions');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleTxTableSortClick = (column: TransactionSortKey) => {
    const prev = txTableSortRef.current;
    const nextDir: 'asc' | 'desc' =
      prev.key === column && prev.dir === 'asc' ? 'desc' : 'asc';
    const next: TransactionTableSort = { key: column, dir: nextDir };
    txTableSortRef.current = next;
    setTxTableSort(next);
    setPagination((p) => ({ ...p, currentPage: 1 }));
    void loadTransactionsWithParams(searchParams, 1, pagination.pageSize);
  };

  const handleSearch = () => {
    // Validate VIN length before searching
    if (searchParams.vin && searchParams.vin.length < 8) {
      setError('VIN must be at least 8 characters long');
      return;
    }
    
    setError(null);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadTransactions(1, pagination.pageSize);
  };

  const handleClearFilters = () => {
    if (!currentUser?.agency) return;

    setError(null);
    const cleared = {
      vin: '',
      agency: canPickAgencyFilter ? '' : currentUser.agency.id,
      status: '',
      clientNumber: '',
      unitNumber: '',
      requestor: '',
      requestReceivedAt: '',
    };
    setSearchParams(cleared);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    loadTransactionCounts(cleared);
    loadTransactionsWithParams(cleared, 1, pagination.pageSize);
  };

  const handleAgencyChange = (agencyId: string) => {
    // Create updated search params with new agency (use current searchParams)
    const updatedParams = { ...searchParams, agency: agencyId };
    
    // Update search params state
    setSearchParams(updatedParams);
    
    // Reset pagination and trigger search
    setError(null);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Load transaction counts immediately with new agency filter
    loadTransactionCounts(updatedParams);
    
    // Load transactions with new agency filter
    loadTransactionsWithParams(updatedParams, 1, pagination.pageSize);
  };

  const handlePageChange = (newPage: number) => {
    loadTransactions(newPage, pagination.pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
    loadTransactions(1, newPageSize);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      // Validate VIN length before searching
      if (searchParams.vin && searchParams.vin.length < 8) {
        setError('VIN must be at least 8 characters long');
        return;
      }
      
      setError(null);
      handleSearch();
    }
  };

  const handleStatusCardClick = (status: string) => {
    const newSearchParams = { ...searchParams, status };
    setSearchParams(newSearchParams);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Trigger search with the new status immediately
    loadTransactionsWithParams(newSearchParams, 1, pagination.pageSize);
  };

  const handleTotalCardClick = () => {
    const newSearchParams = { ...searchParams, status: '' };
    setSearchParams(newSearchParams);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Trigger search without status filter immediately
    loadTransactionsWithParams(newSearchParams, 1, pagination.pageSize);
  };

  const loadTransactionsWithParams = async (
    params: typeof searchParams,
    page: number = pagination.currentPage,
    pageSize: number = pagination.pageSize,
    options?: LoadOptions,
  ) => {
    if (!currentUser?.agency) return;

    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (params.vin) {
        queryParams.append('vin', params.vin);
      }
      if (params.status) {
        queryParams.append('status', params.status);
      }
      if (params.clientNumber) {
        queryParams.append('clientNumber', params.clientNumber);
      }
      if (params.unitNumber) {
        queryParams.append('unitNumber', params.unitNumber);
      }
      if (params.requestor) {
        queryParams.append('requestor', params.requestor);
      }
      if (params.requestReceivedAt) {
        queryParams.append('requestReceivedAt', params.requestReceivedAt);
      }
      if (transactionListScope === 'all') {
        if (params.agency) {
          queryParams.append('agencyId', params.agency);
        }
      } else if (currentUser?.agency) {
        queryParams.append('agencyId', currentUser.agency.id);
      }

      const sortApplied = txTableSortRef.current;
      if (sortApplied.key) {
        queryParams.append('sortBy', sortApplied.key);
        queryParams.append('sortDir', sortApplied.dir);
      }

      const response = await api.get(`/titles/search/paginated?${queryParams.toString()}`);
      const data: SearchResponse = response.data;

      setTransactions(data.data);
      setPagination(data.pagination);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      if (!silent) {
        setError(err.response?.data?.message || 'Failed to load transactions');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    refreshDashboardSilentRef.current = () => {
      if (!currentUser?.agency) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (blockAutoRefreshRef.current) return;
      if (loadingRef.current) return;

      const sp = searchParamsRef.current;
      const pg = paginationRef.current;
      if (sp.vin && sp.vin.length < 8) return;

      void loadTransactionCounts(sp, { silent: true });
      void loadTransactionsWithParams(sp, pg.currentPage, pg.pageSize, { silent: true });
    };
  }, [currentUser?.agency, transactionListScope, loadTransactionCounts, loadTransactionsWithParams]);

  useEffect(() => {
    if (!currentUser?.agency) return;

    const tick = () => refreshDashboardSilentRef.current();
    const intervalId = window.setInterval(tick, DASHBOARD_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUser?.agency]);

  const handleEditClick = (work: Transaction) => {
    setEditingWork(work);
    setEditFormData({ ...work });
    editEtaTouchedRef.current = false;
    setValidationErrors([]);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingWork(null);
    setEditFormData({});
    setValidationErrors([]);
  };

  const handleEditFormChange = (field: keyof Transaction, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setError(null);
    }
  };

  const handleUpdateWork = async () => {
    if (!editingWork) return;

    // Validate form data before saving
    const validationResult = validateTransactionForm(editFormData as any);
    
    if (!validationResult.isValid) {
      setValidationErrors(validationResult.errors);
      setError(validationResult.errors.join(', '));
      return;
    }

    // Clear any previous validation errors
    setValidationErrors([]);
    setError(null);

    try {
      const payload: Record<string, unknown> = { ...(editFormData as object) };
      delete payload.agency;
      delete payload.group;

      // Drop date keys from the naive spread, then copy only from form state. Otherwise a stale
      // `estimatedCompleteAt: null` from an earlier object shape survives the spread and clears the DB.
      for (const key of TRANSACTION_PUT_DATE_KEYS) {
        delete payload[key as string];
      }
      for (const key of TRANSACTION_PUT_DATE_KEYS) {
        const v = editFormData[key];
        if (v !== undefined) {
          payload[key as string] = v;
        }
      }

      // Never omit `status` — JSON.stringify drops undefined and the API would fall back to DB
      // rules that can clear estimatedCompleteAt when the stored status is "Complete".
      payload.status = (editFormData.status ?? editingWork.status) as string;
      payload.vin = (editFormData.vin ?? editingWork.vin) as string;

      // If form state has null ETA but user never touched the control, keep the row's original ETA
      // (avoids PUT null clearing the DB after status dropdown / merge quirks).
      if (
        !editEtaTouchedRef.current &&
        editFormData.estimatedCompleteAt == null &&
        editingWork.estimatedCompleteAt &&
        !isRejectedTransactionStatus(String(payload.status)) &&
        !isCompleteTransactionStatus(String(payload.status))
      ) {
        payload.estimatedCompleteAt = editingWork.estimatedCompleteAt;
      }

      if (isRejectedTransactionStatus(payload.status)) {
        payload.estimatedCompleteAt = null;
      }
      if (isCompleteTransactionStatus(payload.status)) {
        const stamp = utcMidnightIsoForLocalToday();
        if (!payload.processingCompleteAt) payload.processingCompleteAt = stamp;
        payload.estimatedCompleteAt = null;
      }
      const response = await api.put(`/titles/${editingWork.id}`, payload);

      // Update the local state (server enforces null ETA for Rejected)
      const saved = response.data as Transaction;
      setTransactions((prev) =>
        prev.map((work) => (work.id === editingWork.id ? { ...work, ...saved } : work)),
      );
      
      // Refresh the counts
      loadTransactionCounts();
      
      handleEditModalClose();
    } catch (err: any) {
      console.error('Failed to update transaction:', err);
      setError(err.response?.data?.message || 'Failed to update transaction');
    }
  };

  const handleStatusChange = async (transactionId: string, newStatus: string) => {
    if (!canMutateTransactions) return;
    try {
      const completedAt = utcMidnightIsoForLocalToday();
      const body = isRejectedTransactionStatus(newStatus)
        ? { status: newStatus, estimatedCompleteAt: null }
        : isCompleteTransactionStatus(newStatus)
          ? { status: newStatus, processingCompleteAt: completedAt }
          : { status: newStatus };
      await api.put(`/titles/${transactionId}`, body);

      // Update the local state
      setTransactions((prev) =>
        prev.map((work) =>
          work.id === transactionId
            ? {
                ...work,
                status: newStatus,
                ...(isRejectedTransactionStatus(newStatus) ? { estimatedCompleteAt: null } : {}),
                ...(isCompleteTransactionStatus(newStatus)
                  ? { processingCompleteAt: completedAt }
                  : {}),
              }
            : work,
        ),
      );
      
      // Refresh the counts
      loadTransactionCounts();
    } catch (err: any) {
      console.error('Failed to update transaction status:', err);
      setError(err.response?.data?.message || 'Failed to update transaction status');
    }
  };

  const handleViewClick = (work: Transaction) => {
    setViewingWork(work);
    setIsViewModalOpen(true);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setViewingWork(null);
  };

  const handleDeleteClick = (work: Transaction) => {
    setTransactionPendingDelete(work);
  };

  const handleDeleteModalClose = () => {
    if (deleteInProgress) return;
    setTransactionPendingDelete(null);
  };

  const handleDeleteTransactionConfirm = async () => {
    if (!transactionPendingDelete) return;

    setDeleteInProgress(true);
    setError(null);

    try {
      await api.delete(`/titles/${transactionPendingDelete.id}`);
      setTransactionPendingDelete(null);

      const wasOnlyOnPage = transactions.length === 1;
      const nextPage =
        wasOnlyOnPage && pagination.currentPage > 1 ? pagination.currentPage - 1 : pagination.currentPage;

      await loadTransactions(nextPage, pagination.pageSize);
      loadTransactionCounts();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      console.error('Failed to delete transaction:', err);
      setError(ax.response?.data?.message || 'Failed to delete transaction');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleExport = async () => {
    if (!currentUser?.agency) return;

    try {
      const params = new URLSearchParams();

      if (searchParams.vin) {
        params.append('vin', searchParams.vin);
      }
      if (searchParams.status) {
        params.append('status', searchParams.status);
      }
      if (searchParams.clientNumber) {
        params.append('clientNumber', searchParams.clientNumber);
      }
      if (searchParams.unitNumber) {
        params.append('unitNumber', searchParams.unitNumber);
      }
      if (searchParams.requestor) {
        params.append('requestor', searchParams.requestor);
      }
      if (searchParams.requestReceivedAt) {
        params.append('requestReceivedAt', searchParams.requestReceivedAt);
      }
      if (transactionListScope === 'all') {
        if (searchParams.agency) {
          params.append('agencyId', searchParams.agency);
        }
      } else if (currentUser?.agency) {
        params.append('agencyId', currentUser.agency.id);
      }

      const response = await api.get(`/titles/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create blob link and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'transactions_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export transactions:', err);
      setError(err.response?.data?.message || 'Failed to export transactions');
    }
  };

  const editTransactionTypeOptions = useMemo(() => {
    const fromAgency = availableTransactionTypes;
    const current = editFormData.transactionType?.trim();
    if (current && !fromAgency.includes(current)) {
      return [...fromAgency, current];
    }
    return fromAgency;
  }, [availableTransactionTypes, editFormData.transactionType]);

  const editModalEtaFieldDisabled =
    isCompleteTransactionStatus(editFormData.status) || isRejectedTransactionStatus(editFormData.status);
  const editModalEtaFieldTitle = isCompleteTransactionStatus(editFormData.status)
    ? 'Not applicable while status is Complete — same as dashboard grid'
    : isRejectedTransactionStatus(editFormData.status)
      ? 'ETA is not stored for Rejected. Change status to set a date.'
      : undefined;

  /** Opening a Complete record without Processing Complete date — stamp today so the date control shows correctly. */
  useEffect(() => {
    if (!isEditModalOpen || !editingWork) return;
    setEditFormData((prev) => {
      if (!isCompleteTransactionStatus(prev.status)) return prev;
      if (prev.processingCompleteAt) return prev;
      return { ...prev, processingCompleteAt: utcMidnightIsoForLocalToday() };
    });
  }, [isEditModalOpen, editingWork?.id]);

  return (
    <div>
      <h1 style={{ marginBottom: '8px' }}>Dashboard</h1>
      <p style={{ 
        color: '#6c757d', 
        fontSize: '1.1rem', 
        marginBottom: '32px' 
      }}>
        Welcome to the Portal
      </p>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px', 
        marginTop: '24px' 
      }}>
        {/* Total Card */}
        <div 
          className="card"
          style={{ 
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: searchParams.status === '' ? '2px solid var(--primary-color)' : '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: searchParams.status === '' ? 'rgba(252, 80, 3, 0.05)' : 'white',
            boxShadow: searchParams.status === '' ? 'var(--elevation-2)' : 'var(--elevation-1)'
          }}
          onClick={handleTotalCardClick}
          onMouseEnter={(e) => {
            if (searchParams.status !== '') {
              e.currentTarget.style.borderColor = 'var(--primary-color)';
              e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
              e.currentTarget.style.boxShadow = 'var(--elevation-2)';
            }
          }}
          onMouseLeave={(e) => {
            if (searchParams.status !== '') {
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.boxShadow = 'var(--elevation-1)';
            }
          }}
        >
          <h3 style={{ 
            color: 'var(--secondary-color)', 
            marginBottom: '16px',
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            Total
          </h3>
          <p style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: 'var(--primary-color)',
            margin: 0
          }}>
            {countsLoading ? '...' : transactionCounts.total}
          </p>
        </div>

        {/* Dynamic Status Cards */}
        {availableStatuses.map((status) => {
          // Try exact match first, then case-insensitive match
          let count = transactionCounts.byStatus[status] || 0;
          if (count === 0) {
            // Try case-insensitive match
            const matchingKey = Object.keys(transactionCounts.byStatus).find(
              key => key.toLowerCase() === status.toLowerCase()
            );
            if (matchingKey) {
              count = transactionCounts.byStatus[matchingKey];
            }
          }
          
          const isSelected = searchParams.status === status;
          
          // Define colors for different statuses
          const getStatusColor = (status: string) => {
            const statusLower = status.toLowerCase();
            if (statusLower.includes('completed') || statusLower.includes('done')) return '#28a745';
            if (statusLower.includes('pending') || statusLower.includes('waiting')) return '#ffc107';
            if (statusLower.includes('error') || statusLower.includes('failed')) return '#dc3545';
            if (statusLower.includes('processing') || statusLower.includes('in progress')) return '#007bff';
            return '#6c757d'; // default color
          };

          const statusColor = getStatusColor(status);
          const backgroundColor = isSelected ? `${statusColor}15` : 'white';
          const borderColor = isSelected ? statusColor : 'rgba(0, 0, 0, 0.08)';

          return (
            <div 
              key={status}
              className="card" 
              style={{ 
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: `2px solid ${borderColor}`,
                backgroundColor: backgroundColor,
                boxShadow: isSelected ? 'var(--elevation-2)' : 'var(--elevation-1)'
              }}
              onClick={() => handleStatusCardClick(status)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = statusColor;
                  e.currentTarget.style.backgroundColor = `${statusColor}15`;
                  e.currentTarget.style.boxShadow = 'var(--elevation-2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.boxShadow = 'var(--elevation-1)';
                }
              }}
            >
              <h3 style={{ 
                color: 'var(--secondary-color)', 
                marginBottom: '16px',
                fontSize: '1.1rem',
                fontWeight: '600',
                wordBreak: 'break-word'
              }}>
                {status}
              </h3>
              <p style={{ 
                fontSize: '1.75rem', 
                fontWeight: '700', 
                color: statusColor,
                margin: 0
              }}>
                {countsLoading ? '...' : count}
              </p>
            </div>
          );
        })}
      </div>

      {/* Search Section */}
      <div style={{ marginTop: '48px' }}>
        <h2 style={{ marginBottom: '8px' }}></h2>
        <p style={{ 
          color: '#6c757d', 
          marginBottom: '32px',
          fontSize: '1.1rem'
        }}>
          Search for transaction records by VIN, unit number, client number, requestor, agency, or status
        </p>
        
        <div className="card" style={{ marginBottom: '32px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                VIN
              </label>
              <input
                type="text"
                placeholder="Enter VIN number (min 8 characters)"
                value={searchParams.vin}
                onChange={(e) => setSearchParams({ ...searchParams, vin: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                Client #
              </label>
              <input
                type="text"
                placeholder="Enter client number"
                value={searchParams.clientNumber}
                onChange={(e) => setSearchParams({ ...searchParams, clientNumber: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                Unit Number
              </label>
              <input
                type="text"
                placeholder="Enter unit number"
                value={searchParams.unitNumber}
                onChange={(e) => setSearchParams({ ...searchParams, unitNumber: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                Requestor
              </label>
              <input
                type="text"
                placeholder="Enter requestor"
                value={searchParams.requestor}
                onChange={(e) => setSearchParams({ ...searchParams, requestor: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                Request received on or after
              </label>
              <input
                type="date"
                value={searchParams.requestReceivedAt}
                onChange={(e) => setSearchParams({ ...searchParams, requestReceivedAt: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            
            {canPickAgencyFilter && (
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  color: 'var(--secondary-color)'
                }}>
                  Agency
                </label>
                <select
                  value={searchParams.agency}
                  onChange={(e) => handleAgencyChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ width: '100%' }}
                >
                  <option value="">All Agencies</option>
                  {agencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: 'var(--secondary-color)'
              }}>
                Status
              </label>
              <select
                value={searchParams.status}
                onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                onKeyDown={handleKeyDown}
                style={{ width: '100%' }}
              >
                <option value="">All Statuses</option>
                {availableStatuses.length > 0 ? (
                  availableStatuses.map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No statuses configured</option>
                )}
              </select>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={handleSearch}
              className="btn-primary"
              style={{
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'none',
                border: '1px solid #015F93',
                color: '#015F93',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span className="material-symbols-outlined">search</span>
              Search
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className="btn-outline"
              style={{
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '8px',
              }}
            >
              <span className="material-symbols-outlined">filter_alt_off</span>
              Clear filters
            </button>
          </div>
        </div>
        
        {/* Results Grid */}
        <div className="card">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0,
              color: 'var(--secondary-color)',
              fontSize: '1.25rem',
              fontFamily: 'var(--font-family)',
              fontWeight: 600,
              lineHeight: 1.4,
            }}>
              Transactions ({pagination.totalCount})
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleExport}
                disabled={transactions.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  background: 'none',
                  border: '1px solid #28a745',
                  color: '#28a745',
                  borderRadius: '4px',
                  cursor: transactions.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  transition: 'all 0.2s ease',
                  opacity: transactions.length === 0 ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (transactions.length > 0) {
                    e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (transactions.length > 0) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title="Export all matching records to Excel"
                aria-label="Export to Excel"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>download</span>
                Export
              </button>
              <label style={{
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                fontWeight: 400,
                lineHeight: 1.5,
                color: 'var(--secondary-color)',
              }}>
                Rows per page:
              </label>
              <select
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: 1.5,
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {error && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--secondary-color)',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              lineHeight: 1.5,
            }}>
              Loading...
            </div>
          ) : transactions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6c757d',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              lineHeight: 1.5,
            }}>
              No transactions found
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  ...TX_GRID_TEXT,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: 'var(--elevation-1)',
                }}>
                  <thead>
                    <tr style={{
                      backgroundColor: 'rgba(1, 95, 147, 0.05)',
                      borderBottom: '2px solid rgba(1, 95, 147, 0.1)',
                    }}>
                      <TransactionsSortableColumnHeader
                        column="vin"
                        label="VIN"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      {isSystemAdmin && (
                        <TransactionsSortableColumnHeader
                          column="agency"
                          label="Agency"
                          sort={txTableSort}
                          onSort={handleTxTableSortClick}
                        />
                      )}
                      {showGroupColumn && (
                        <TransactionsSortableColumnHeader
                          column="group"
                          label="Group"
                          sort={txTableSort}
                          onSort={handleTxTableSortClick}
                        />
                      )}
                      <TransactionsSortableColumnHeader
                        column="clientNumber"
                        label="Client #"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="unitNumber"
                        label="Unit Number"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="eta"
                        label="ETA"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="status"
                        label="Status"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="completedAt"
                        label="Completed At"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="transactionType"
                        label="Transaction Type"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="state"
                        label="State"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="vendor"
                        label="Vendor"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="requestor"
                        label="Requestor"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <TransactionsSortableColumnHeader
                        column="requestReceived"
                        label="Request Received"
                        sort={txTableSort}
                        onSort={handleTxTableSortClick}
                      />
                      <th
                        style={txGridHeaderStyle({
                          textAlign: 'center',
                          width: `${actionsColumnWidthPx}px`,
                        })}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((work, index) => (
                      <tr key={work.id} style={{ 
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: index % 2 === 0 ? 'white' : 'rgba(0, 0, 0, 0.02)'
                      }}>
                        <td style={txGridCellStyle()}>
                          {work.vin}
                        </td>
                        {isSystemAdmin && (
                          <td style={txGridCellStyle()}>
                            {work.agency?.name || '-'}
                          </td>
                        )}
                        {showGroupColumn && (
                          <td style={txGridCellStyle()}>
                            {work.group ? (
                              work.group.name
                            ) : (
                              <span style={{ color: '#6c757d' }}>—</span>
                            )}
                          </td>
                        )}
                        <td style={txGridCellStyle()}>
                          {work.clientNumber || '-'}
                        </td>
                        <td style={txGridCellStyle()}>
                          {work.unitNumber || '-'}
                        </td>
                        <td style={txGridCellStyle({ color: '#6c757d' })}>
                          {isCompleteTransactionStatus(work.status) || isRejectedTransactionStatus(work.status)
                            ? '-'
                            : formatDateString(work.estimatedCompleteAt)}
                        </td>
                        <td style={txGridCellStyle()}>
                          {canMutateTransactions ? (
                            <select
                              value={work.status}
                              onChange={(e) => handleStatusChange(work.id, e.target.value)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '16px',
                                fontSize: '14px',
                                fontFamily: 'var(--font-family)',
                                lineHeight: 1.5,
                                fontWeight: 600,
                                backgroundColor: 'rgba(1, 95, 147, 0.1)',
                                color: 'var(--secondary-color)',
                                border: '1px solid rgba(1, 95, 147, 0.2)',
                                cursor: 'pointer',
                                minWidth: '120px',
                                outline: 'none',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.15)';
                                e.currentTarget.style.borderColor = 'rgba(1, 95, 147, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.1)';
                                e.currentTarget.style.borderColor = 'rgba(1, 95, 147, 0.2)';
                              }}
                              title="Click to change status"
                            >
                              {(() => {
                                const statusSet = new Set([work.status, ...availableStatuses]);
                                return Array.from(statusSet).map((status, index) => (
                                  <option key={index} value={status}>
                                    {status}
                                  </option>
                                ));
                              })()}
                            </select>
                          ) : (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '6px 12px',
                                borderRadius: '16px',
                                fontSize: '14px',
                                fontFamily: 'var(--font-family)',
                                lineHeight: 1.5,
                                fontWeight: 600,
                                backgroundColor: 'rgba(108, 117, 125, 0.12)',
                                color: 'var(--secondary-color)',
                                border: '1px solid rgba(108, 117, 125, 0.25)',
                                minWidth: '120px',
                              }}
                            >
                              {work.status}
                            </span>
                          )}
                        </td>
                        <td style={txGridCellStyle({ color: '#6c757d' })}>
                          {formatDateString(work.processingCompleteAt)}
                        </td>
                        <td style={txGridCellStyle()}>
                          {work.transactionType || '-'}
                        </td>
                        <td style={txGridCellStyle()}>
                          {work.registrationState || '-'}
                        </td>
                        <td style={txGridCellStyle()}>
                          {work.vendorName || '-'}
                        </td>
                        <td style={txGridCellStyle()}>
                          {work.requestor || '-'}
                        </td>
                        <td style={txGridCellStyle({ color: '#6c757d' })}>
                          {formatDateString(work.requestReceivedAt)}
                        </td>
                        <td style={txGridCellStyle({ textAlign: 'center' })}>
                          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleViewClick(work)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              title="View record details"
                              aria-label="View record details"
                            >
                              <span className="material-symbols-outlined success">visibility</span>
                            </button>
                            {canMutateTransactions && (
                              <button
                                onClick={() => handleEditClick(work)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                title="Edit record"
                                aria-label="Edit record"
                              >
                                <span className="material-symbols-outlined primary">edit</span>
                              </button>
                            )}
                            {isAdmin && canMutateTransactions && (
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(work)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  color: '#dc3545',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.08)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                title="Delete transaction"
                                aria-label="Delete transaction"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>
                                  delete
                                </span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <div style={{
                  color: '#6c757d',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: 1.5,
                }}>
                  Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of{' '}
                  {pagination.totalCount} entries
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: pagination.hasPreviousPage ? 'white' : '#f8f9fa',
                      color: pagination.hasPreviousPage ? '#333' : '#6c757d',
                      cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      lineHeight: 1.5,
                    }}
                  >
                    Previous
                  </button>
                  <span style={{
                    padding: '8px 12px',
                    color: '#6c757d',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    lineHeight: 1.5,
                  }}>
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: pagination.hasNextPage ? 'white' : '#f8f9fa',
                      color: pagination.hasNextPage ? '#333' : '#6c757d',
                      cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      lineHeight: 1.5,
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {transactionPendingDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          role="presentation"
          onClick={handleDeleteModalClose}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-transaction-title"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '440px',
              width: '90%',
              boxShadow: 'var(--elevation-2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
              <span
                className="material-symbols-outlined"
                style={{ fontSize: '28px', color: '#dc3545', flexShrink: 0 }}
                aria-hidden
              >
                warning
              </span>
              <div>
                <h2
                  id="delete-transaction-title"
                  style={{ margin: '0 0 8px', color: 'var(--secondary-color)', fontSize: '1.25rem' }}
                >
                  Delete transaction?
                </h2>
                <p style={{ margin: 0, color: '#495057', fontSize: '14px', lineHeight: 1.5 }}>
                  This will permanently remove this row from the dashboard. This action cannot be undone.
                </p>
                <p
                  style={{
                    margin: '12px 0 0',
                    padding: '10px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#212529',
                  }}
                >
                  VIN {transactionPendingDelete.vin}
                  {transactionPendingDelete.clientNumber
                    ? ` · Client #${transactionPendingDelete.clientNumber}`
                    : ''}
                </p>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <button
                type="button"
                disabled={deleteInProgress}
                onClick={handleDeleteModalClose}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #015F93',
                  color: '#015F93',
                  borderRadius: '8px',
                  cursor: deleteInProgress ? 'not-allowed' : 'pointer',
                  opacity: deleteInProgress ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteInProgress}
                onClick={() => void handleDeleteTransactionConfirm()}
                style={{
                  padding: '10px 20px',
                  background: '#dc3545',
                  border: '1px solid #dc3545',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: deleteInProgress ? 'not-allowed' : 'pointer',
                  opacity: deleteInProgress ? 0.85 : 1,
                  fontWeight: 600,
                }}
              >
                {deleteInProgress ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && viewingWork && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '16px'
            }}>
              <h2 style={{ margin: 0, color: 'var(--secondary-color)' }}>
                Transaction Record Details
              </h2>
              <button
                onClick={handleViewModalClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Close modal"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {/* Basic Information */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  VIN
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontFamily: 'monospace' }}>
                  {viewingWork.vin}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Vendor Name
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.vendorName || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Client Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.clientNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Unit Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.unitNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Registration State
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.registrationState || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Transaction Type
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.transactionType || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Requestor
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.requestor || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Request Tracking Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.requestTrackingNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Status
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: '#e9ecef',
                    color: '#495057'
                  }}>
                    {viewingWork.status}
                  </span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Agency Tracking Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.agencyTrackingNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Temp Tag Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.tempTagNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  License Plate Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.licensePlateNumber || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  License Plate State
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.licensePlateState || '-'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Invoice Number
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.invoiceNumber || '-'}
                </div>
              </div>

              {/* Date Fields */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Request Received At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {formatDateString(viewingWork.requestReceivedAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Estimated Complete At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {isCompleteTransactionStatus(viewingWork.status) || isRejectedTransactionStatus(viewingWork.status)
                    ? '-'
                    : formatDateString(viewingWork.estimatedCompleteAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Processing Complete At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {formatDateString(viewingWork.processingCompleteAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Sent Agency At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {formatDateString(viewingWork.sentAgencyAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Temp Tag Expiration At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {formatDateString(viewingWork.tempTagExpirationAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  License Plate Expiration At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {formatDateString(viewingWork.licensePlateExpirationAt)}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Created At
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {new Date(viewingWork.createdAt).toLocaleString()}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                  Agency
                </label>
                <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingWork.agency.name} ({viewingWork.agency.code})
                </div>
              </div>
            </div>

            {/* Notes Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#6c757d' }}>
                Notes
              </label>
              <div style={{ 
                padding: '8px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                minHeight: '60px',
                whiteSpace: 'pre-wrap'
              }}>
                {viewingWork.notes || '-'}
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e0e0e0',
              paddingTop: '16px'
            }}>
              <button
                onClick={handleViewModalClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #015F93',
                  color: '#015F93',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingWork && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '16px'
            }}>
              <h2 style={{ margin: 0, color: 'var(--secondary-color)' }}>
                Edit Record
              </h2>
              <button
                onClick={handleEditModalClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Close modal"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span className="material-symbols-outlined" style={{
                    color: '#856404',
                    fontSize: '20px'
                  }}>
                    warning
                  </span>
                  <h3 style={{
                    margin: 0,
                    color: '#856404',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    Validation Errors ({validationErrors.length})
                  </h3>
                </div>
                <ul style={{
                  margin: 0,
                  paddingLeft: '24px',
                  color: '#856404',
                  fontSize: '14px'
                }}>
                  {validationErrors.map((error, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {/* Basic Information */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  VIN *
                </label>
                <input
                  type="text"
                  value={editFormData.vin || ''}
                  onChange={(e) => handleEditFormChange('vin', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Vendor Name
                </label>
                <input
                  type="text"
                  value={editFormData.vendorName || ''}
                  onChange={(e) => handleEditFormChange('vendorName', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Client Number
                </label>
                <input
                  type="text"
                  value={editFormData.clientNumber || ''}
                  onChange={(e) => handleEditFormChange('clientNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Unit Number
                </label>
                <input
                  type="text"
                  value={editFormData.unitNumber || ''}
                  onChange={(e) => handleEditFormChange('unitNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Registration State
                </label>
                <select
                  value={editFormData.registrationState || ''}
                  onChange={(e) => handleEditFormChange('registrationState', e.target.value || null)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select State</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Transaction Type
                </label>
                <select
                  value={editFormData.transactionType || ''}
                  onChange={(e) => handleEditFormChange('transactionType', e.target.value || undefined)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select Transaction Type</option>
                  {editTransactionTypeOptions.map((tt, index) => (
                    <option key={`${tt}-${index}`} value={tt}>
                      {tt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Requestor
                </label>
                <input
                  type="text"
                  value={editFormData.requestor || ''}
                  onChange={(e) => handleEditFormChange('requestor', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Request Tracking Number
                </label>
                <input
                  type="text"
                  value={editFormData.requestTrackingNumber || ''}
                  onChange={(e) => handleEditFormChange('requestTrackingNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Status *
                </label>
                <select
                  value={editFormData.status || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const stamp = utcMidnightIsoForLocalToday();
                    setEditFormData((prev) => ({
                      ...prev,
                      status: v,
                      ...(isRejectedTransactionStatus(v) ? { estimatedCompleteAt: null } : {}),
                      // Do not clear estimatedCompleteAt when selecting Complete — UI hides ETA but keeping
                      // the prior ISO in state avoids sending null after switching back to In Process et al.
                      ...(isCompleteTransactionStatus(v)
                        ? {
                            processingCompleteAt: stamp,
                          }
                        : {}),
                    }));
                    if (validationErrors.length > 0) {
                      setValidationErrors([]);
                      setError(null);
                    }
                  }}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select Status</option>
                  {availableStatuses.map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Agency Tracking Number
                </label>
                <input
                  type="text"
                  value={editFormData.agencyTrackingNumber || ''}
                  onChange={(e) => handleEditFormChange('agencyTrackingNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Temp Tag Number
                </label>
                <input
                  type="text"
                  value={editFormData.tempTagNumber || ''}
                  onChange={(e) => handleEditFormChange('tempTagNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  License Plate Number
                </label>
                <input
                  type="text"
                  value={editFormData.licensePlateNumber || ''}
                  onChange={(e) => handleEditFormChange('licensePlateNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  License Plate State
                </label>
                <select
                  value={editFormData.licensePlateState || ''}
                  onChange={(e) => handleEditFormChange('licensePlateState', e.target.value || null)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select State</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Invoice Number
                </label>
                <input
                  type="text"
                  value={editFormData.invoiceNumber || ''}
                  onChange={(e) => handleEditFormChange('invoiceNumber', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              {/* Date Fields */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Request Received At
                </label>
                <input
                  type="date"
                  value={toDateInputValue(editFormData.requestReceivedAt)}
                  onChange={(e) =>
                    handleEditFormChange('requestReceivedAt', e.target.value ? dateInputToIso(e.target.value) : null)
                  }
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Estimated Complete At
                </label>
                <input
                  type="date"
                  value={
                    editModalEtaFieldDisabled
                      ? ''
                      : toDateInputValue(editFormData.estimatedCompleteAt)
                  }
                  onChange={(e) => {
                    editEtaTouchedRef.current = true;
                    handleEditFormChange(
                      'estimatedCompleteAt',
                      e.target.value ? dateInputToIso(e.target.value) : null,
                    );
                  }}
                  disabled={editModalEtaFieldDisabled}
                  title={editModalEtaFieldTitle}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    opacity: editModalEtaFieldDisabled ? 0.65 : 1,
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Processing Complete At
                </label>
                <input
                  type="date"
                  key={`edit-pc-${editFormData.status}-${toDateInputValue(editFormData.processingCompleteAt) || 'none'}`}
                  value={toDateInputValue(editFormData.processingCompleteAt)}
                  onChange={(e) =>
                    handleEditFormChange('processingCompleteAt', e.target.value ? dateInputToIso(e.target.value) : null)
                  }
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Sent Agency At
                </label>
                <input
                  type="date"
                  value={toDateInputValue(editFormData.sentAgencyAt)}
                  onChange={(e) =>
                    handleEditFormChange('sentAgencyAt', e.target.value ? dateInputToIso(e.target.value) : null)
                  }
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Temp Tag Expiration At
                </label>
                <input
                  type="date"
                  value={toDateInputValue(editFormData.tempTagExpirationAt)}
                  onChange={(e) =>
                    handleEditFormChange('tempTagExpirationAt', e.target.value ? dateInputToIso(e.target.value) : null)
                  }
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  License Plate Expiration At
                </label>
                <input
                  type="date"
                  value={toDateInputValue(editFormData.licensePlateExpirationAt)}
                  onChange={(e) =>
                    handleEditFormChange(
                      'licensePlateExpirationAt',
                      e.target.value ? dateInputToIso(e.target.value) : null,
                    )
                  }
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>

            {/* Notes Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Notes
              </label>
              <textarea
                value={editFormData.notes || ''}
                onChange={(e) => handleEditFormChange('notes', e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
              />
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e0e0e0',
              paddingTop: '16px'
            }}>
              <button
                onClick={handleEditModalClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #015F93',
                  color: '#015F93',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleUpdateWork}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: 'none',
                  border: '1px solid #FC5003',
                  color: '#FC5003',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-symbols-outlined small">save</span>
                Update Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;


